import { commonFacebookEventsUtil } from '@/shared/features/analytics/utils/facebookCommonAttributes';
import { getCookie } from '@/core/utils/cookieUtils';

export interface FacebookSearchSuggestionProduct {
  designId?: number | string;
  id?: number | string;
  productId?: number | string;
  sku?: number | string;
}

export interface FacebookSearchDesign {
  designId: number;
}

export interface FacebookSearchEventData {
  search_text?: string;
  products?: {
    list?: FacebookSearchSuggestionProduct[];
  };
  designs?: FacebookSearchDesign[];
  result_count?: number;
  sort_order?: string;
  /** Listing header / h1 text. Used as surface_title on search PLP. */
  surface_title?: string;
}

let fbSearchDebounce: ReturnType<typeof setTimeout> | undefined;

function isSearchPlpPath(): boolean {
  if (typeof window === 'undefined') return false;
  const pathname = window.location.pathname;
  return pathname === '/search' || pathname.startsWith('/search/');
}

function getSearchHeadingText(): string {
  if (typeof document === 'undefined') return '';
  const heading = document.querySelector('h1');
  return heading?.textContent?.replace(/\s+/g, ' ').trim() ?? '';
}

function resolveSearchSurfaceTitle(
  explicitTitle?: string,
  searchString?: string | null,
): string | undefined {
  const fromHeading = getSearchHeadingText();
  if (fromHeading) return fromHeading;

  const fromListing = explicitTitle?.trim();
  if (fromListing) return fromListing.toUpperCase();

  if (searchString?.trim()) {
    return `SEARCH RESULTS FOR "${searchString.trim().toUpperCase()}"`;
  }

  return undefined;
}

/**
 * Meta Pixel - Search Event
 *
 * Triggers the Meta "Search" standard event when a user performs a meaningful
 * search and search results are available.
 */
export function facebookSearchEvent(data?: FacebookSearchEventData): void {
  const triggerEvent = (source: 'suggestion' | 'page') => {
    const searchType = typeof window !== 'undefined' && window.location.pathname.includes('/search/image') ? 'Image' : 'Text';

    const searchString =
      new URLSearchParams(window.location.search).get('search_query') ||
      data?.search_text;

    if (searchType !== 'Image' && !searchString) {
      return;
    }

    let contentIds: string[] = [];

    if (data?.products?.list?.length) {
      contentIds = data.products.list
        .slice(0, 9)
        .map((item) => item.designId ?? item.id ?? item.productId ?? item.sku)
        .filter((id) => id != null && id !== '')
        .map(String);
    } else if (data?.designs?.length) {
      contentIds = data.designs
        .slice(0, 9)
        .map((item) => item.designId)
        .filter(Boolean)
        .map(String);
    } else {
      const productCards = document.querySelectorAll<HTMLElement>(
        '#search-result li:not(.valentine-wdg)',
      );
      contentIds = [...productCards]
        .slice(0, 9)
        .map((card) => card.dataset.pid)
        .filter((id): id is string => Boolean(id));
    }

    const resultCount = data?.result_count ?? (data?.products?.list?.length || contentIds.length);
    const resolvedSortOrder = data?.sort_order || getCookie('sortingPreference');
    const surfaceTitle = isSearchPlpPath()
      ? resolveSearchSurfaceTitle(data?.surface_title, searchString)
      : undefined;

    const payload = {
      content_type: 'product',
      content_ids: contentIds,
      search_source: source,
      result_count: resultCount,
      ...(searchType !== 'Image' && searchString ? { search_string: searchString } : {}),
      ...(resolvedSortOrder ? { sort_order: resolvedSortOrder } : {}),
      ...(surfaceTitle ? { surface_title: surfaceTitle } : {}),
    };

    commonFacebookEventsUtil('Search', payload);
  };

  if (data?.products?.list?.length) {
    clearTimeout(fbSearchDebounce);
    fbSearchDebounce = setTimeout(() => {
      triggerEvent('suggestion');
    }, 1000);
    return;
  }

  triggerEvent('page');
}
